// LoadingSplash — silent rail-scan hairline. A 2px track with a sweeping
// highlight gradient that travels left → right on the --ease-mist curve.
// The design system's hairline + motion language is enough of a
// "proof of life" signal that an explicit text label is redundant.
// Reserves min-height so callsites don't shift layout when the splash
// appears or disappears.
//
// Slow-network hint (activity-day hardening): when `timeoutMs` is given,
// a splash that stays up past that threshold reveals a quiet "·連線似乎
// 有點慢" line + a 重新整理 button below the rail (the rail keeps
// scanning as proof-of-life). Default `timeoutMs=undefined` → no timer,
// behavior is byte-for-byte identical to before, so the many callsites
// that don't opt in (Playlist / Fragments / Messages / MapView) are
// untouched. Only the auth/config gates + route Suspense pass a value.

import { useEffect, useState } from "react";
import { T } from "../tokens";
import { TextBtn } from "./TextBtn";

export interface LoadingSplashProps {
  /** Reserved vertical space to avoid layout shift. Default 120px. */
  minHeight?: number;
  /** Track width in px. Default 96. */
  width?: number;
  /** Color of the moving highlight. Default ink-700. */
  color?: string;
  /**
   * When set, show a slow-network hint if the splash is still mounted
   * after this many ms. Omit to disable (default behavior, no timer).
   */
  timeoutMs?: number;
  /**
   * Called once when the slow threshold is crossed. If omitted, the hint
   * still appears and its button does a full window reload.
   */
  onTimeout?: () => void;
}

export function LoadingSplash({
  minHeight = 120,
  width = 96,
  color = T.ink700,
  timeoutMs,
  onTimeout,
}: LoadingSplashProps) {
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    if (timeoutMs === undefined) return;
    const id = window.setTimeout(() => {
      setSlow(true);
      onTimeout?.();
    }, timeoutMs);
    return () => window.clearTimeout(id);
  }, [timeoutMs, onTimeout]);

  return (
    <div
      role="status"
      aria-label="loading"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 20,
        minHeight,
        padding: "20px 0",
      }}
    >
      <div
        style={{
          position: "relative",
          width,
          height: 2,
          background: T.ink100,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(90deg, transparent 0%, ${color} 50%, transparent 100%)`,
            animation: "gb-rail-scan 2400ms var(--ease-mist) infinite",
            willChange: "transform, opacity",
          }}
        />
      </div>

      {slow && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
            animation: "ruifang-fade-in 560ms var(--ease-mist) both",
          }}
        >
          <span
            style={{
              fontFamily: T.mono,
              fontSize: 9,
              letterSpacing: "0.18em",
              color: T.ink350,
              textTransform: "uppercase",
            }}
          >
            · 連 線 似 乎 有 點 慢
          </span>
          <TextBtn
            onClick={() => window.location.reload()}
            mono
            size={11}
            ls="0.2em"
            color={T.ink700}
          >
            · 重 新 整 理
          </TextBtn>
        </div>
      )}
    </div>
  );
}
